// Message keys for the three ways an IBAN can be malformed. Only one is ever
// returned at a time. They are Contentful label keys in the enrollment2
// namespace, rendered by YlFormGroup through t(field.error).
export const IbanFormatError = 'IbanFormatError';
export const IbanLengthError = 'IbanLengthError';
export const IbanChecksumError = 'IbanChecksumError';

export type IbanError = typeof IbanFormatError | typeof IbanLengthError | typeof IbanChecksumError;

/** Total IBAN length per ISO country code, from the SWIFT IBAN registry. */
const ibanLengthsByCountry: { [countryCode: string]: number } = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SC: 31,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20
};

/** Shortest and longest IBAN the standard allows, used when the country is not in the table. */
const MinIbanLength = 15;
const MaxIbanLength = 34;

const ibanStructure = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;

/**
 * Strips the spaces and lower case that members routinely paste in. Worldline
 * accepts both, so this is normalisation and never a reason to reject a value.
 */
export function normalizeIban(value: string): string {
  return value.replace(/[\s -]/g, '').toUpperCase();
}

/**
 * ISO 7064 mod-97-10: move the first four characters to the end, replace each
 * letter with its position in the alphabet plus 9, and check the remainder is 1.
 * Reduced in chunks so the value never exceeds Number.MAX_SAFE_INTEGER.
 */
function hasValidCheckDigits(iban: string): boolean {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;

  for (const character of rearranged) {
    const digits = character >= 'A' && character <= 'Z' ? String(character.charCodeAt(0) - 55) : character;
    remainder = Number(String(remainder) + digits) % 97;
  }

  return remainder === 1;
}

/**
 * Returns the failed check for an already-normalized IBAN, or null when it is
 * well formed. An empty value is left to the required validator.
 */
export function findIbanError(normalizedIban: string): IbanError | null {
  if (!normalizedIban) {
    return null;
  }

  if (!ibanStructure.test(normalizedIban)) {
    return IbanFormatError;
  }

  const expectedLength = ibanLengthsByCountry[normalizedIban.slice(0, 2)];

  if (expectedLength) {
    if (normalizedIban.length !== expectedLength) {
      return IbanLengthError;
    }
  } else if (normalizedIban.length < MinIbanLength || normalizedIban.length > MaxIbanLength) {
    // Country is not in the table, so only the structural bounds can be checked.
    return IbanLengthError;
  }

  return hasValidCheckDigits(normalizedIban) ? null : IbanChecksumError;
}

/**
 * Rejects malformed IBANs before they reach Worldline, which returns them as
 * 420080 INVALID_IBAN with no detail the member can act on (ECOM6-1864).
 */
export function ibanValidator() {
  return (value: string) => {
    const normalized = normalizeIban(value || '');

    // required() only rejects a genuinely empty string, so a value of nothing but
    // spaces or dashes would otherwise pass both validators and submit an empty IBAN.
    if (!normalized) {
      return value ? IbanFormatError : false;
    }

    return findIbanError(normalized) || false;
  };
}
